import * as React from 'react';
import { useSelector } from 'react-redux';
import Alert from 'antd/lib/alert';
import Button from 'antd/lib/button';
import Modal from 'antd/lib/modal';
import Space from 'antd/lib/space';
import Tooltip from 'antd/lib/tooltip';
import Text from 'antd/lib/typography/Text';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import CloseCircleOutlined from '@ant-design/icons/CloseCircleOutlined';
import ExclamationCircleOutlined from '@ant-design/icons/ExclamationCircleOutlined';
import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined';

import * as API from '../../api';
import { RootState } from '@/reducers/reducers';
import {
    VCSDirectPushBranchTarget,
    VCSDirectPushCaveat,
    VCSDirectPushCheckResult,
    VCSDirectPushOperation,
    VCSDirectPushPrerequisite,
} from '../../types/automation';
import { antGold, antGreen, antGrey, antRed, runwayColors, titleStyle } from '../styles/globalStyles';

interface Props {
    orgId: string;
    appId: string;
    // Committing to a branch that already exists, unless told otherwise.
    operation?: VCSDirectPushOperation;
    // The branch is either a role Runway resolves, or named outright when it doesn't exist yet.
    branchTarget?: VCSDirectPushBranchTarget | null;
    branch?: string | null;
    disabledReason?: string;
    createsMergeCommit?: boolean;
    canWaitForRequiredStatusChecks?: boolean;
}

export default function VCSDirectPushCheck({
    orgId,
    appId,
    operation = 'commit',
    branchTarget,
    branch,
    disabledReason,
    createsMergeCommit = false,
    canWaitForRequiredStatusChecks = false,
}: Props): JSX.Element {
    const authCredentials = useSelector((state: RootState) => state.auth.authCredentials);

    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [hasRun, setHasRun] = React.useState(false);
    const [result, setResult] = React.useState<VCSDirectPushCheckResult | null>(null);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const [unavailableReason, setUnavailableReason] = React.useState<string | null>(null);

    const clearResult = React.useCallback(() => {
        setResult(null);
        setErrorMessage(null);
        setUnavailableReason(null);
        setHasRun(false);
    }, []);

    React.useEffect(() => {
        clearResult();
    }, [
        appId,
        branch,
        branchTarget,
        canWaitForRequiredStatusChecks,
        clearResult,
        createsMergeCommit,
        operation,
        orgId,
    ]);

    const runCheck = () => {
        if ((!branchTarget && !branch) || !authCredentials) {
            return;
        }

        clearResult();
        setIsLoading(true);

        API.checkVCSDirectPush(
            {
                orgId,
                appId,
                operation,
                branchTarget: branchTarget ?? undefined,
                branch: branch ?? undefined,
                createsMergeCommit,
                canWaitForRequiredStatusChecks,
            },
            authCredentials,
        )
            .then((response) => {
                if (!response.available || !response.result) {
                    setUnavailableReason(response.unavailableReason ?? 'Runway cannot check this repository.');
                    return;
                }
                setResult(response.result);
                return;
            })
            .catch((e) => setErrorMessage(e.message))
            .finally(() => {
                setHasRun(true);
                setIsLoading(false);
                setIsModalOpen(true);
            });
    };

    const isDisabled = !!disabledReason || (!branchTarget && !branch) || !authCredentials;
    const button = (
        <Button onClick={runCheck} loading={isLoading} disabled={isDisabled}>
            {isLoading ?
                'Testing'
            : hasRun ?
                'Test again'
            :   'Run a test to check prerequisites'}
        </Button>
    );

    return (
        <Space size={12} align="center">
            {disabledReason ?
                <Tooltip title={disabledReason}>
                    <span>{button}</span>
                </Tooltip>
            :   button}

            <Summary
                isLoading={isLoading}
                hasRun={hasRun}
                result={result}
                errorMessage={errorMessage}
                unavailableReason={unavailableReason}
                onClick={() => setIsModalOpen(true)}
            />

            <VCSDirectPushCheckModal
                open={isModalOpen}
                operation={operation}
                result={result}
                errorMessage={errorMessage}
                unavailableReason={unavailableReason}
                onClose={() => setIsModalOpen(false)}
            />
        </Space>
    );
}

function Summary(props: {
    isLoading: boolean;
    hasRun: boolean;
    result: VCSDirectPushCheckResult | null;
    errorMessage: string | null;
    unavailableReason: string | null;
    onClick: () => void;
}): JSX.Element | null {
    const { isLoading, hasRun, result, errorMessage, unavailableReason, onClick } = props;

    if (isLoading || !hasRun) {
        return null;
    }

    if (errorMessage || unavailableReason) {
        return <Text type="secondary">Couldn&apos;t check</Text>;
    }

    if (!result) {
        return null;
    }

    const passed = result.status === 'passed';
    const warning = result.status === 'warning';
    const color =
        passed ? antGreen.green6
        : warning ? antGold.gold6
        : antRed.red6;
    const label =
        passed ? 'Passed'
        : warning ? 'Needs review'
        : 'Not passed';

    return (
        <Space size={6} align="center" style={{ cursor: 'pointer' }} onClick={onClick}>
            {passed ?
                <CheckCircleOutlined style={{ color }} />
            : warning ?
                <ExclamationCircleOutlined style={{ color }} />
            :   <CloseCircleOutlined style={{ color }} />}
            <Text style={{ color }}>{label}</Text>
        </Space>
    );
}

interface ModalProps {
    open: boolean;
    operation: VCSDirectPushOperation;
    result: VCSDirectPushCheckResult | null;
    errorMessage: string | null;
    unavailableReason: string | null;
    onClose: () => void;
}

function VCSDirectPushCheckModal(props: ModalProps): JSX.Element {
    const { open, operation, result, errorMessage, unavailableReason, onClose } = props;

    return (
        <Modal
            open={open}
            title={
                operation === 'createBranch' ? 'Branch creation prerequisites' : 'Bypass pull requests prerequisites'
            }
            onCancel={onClose}
            onOk={onClose}
            cancelButtonProps={{ style: { display: 'none' } }}
            okText="Done"
            width={640}
        >
            {unavailableReason ?
                <Alert type="info" showIcon message={unavailableReason} />
            :   <Space direction="vertical" size={24} style={{ width: '100%' }}>
                    <PoliciesSection operation={operation} result={result} errorMessage={errorMessage} />
                    {result?.guidance.caveat && <CaveatSection caveat={result.guidance.caveat} />}
                </Space>
            }
        </Modal>
    );
}

function PoliciesSection(props: {
    operation: VCSDirectPushOperation;
    result: VCSDirectPushCheckResult | null;
    errorMessage: string | null;
}): JSX.Element {
    const { operation, result, errorMessage } = props;

    return (
        <div>
            <SectionTitle title={result?.guidance.policiesLabel ?? 'Branch rules'} />

            {errorMessage && <Alert type="error" showIcon message={errorMessage} />}

            {!errorMessage && result && (
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    <Alert type={alertType(result)} showIcon message={verdictMessage(operation, result)} />

                    {groupPrerequisitesByPolicy(result.prerequisites).map((group) => (
                        <PolicyCard key={group.policyLabel} group={group} policyNoun={result.guidance.policyNoun} />
                    ))}

                    <CheckedCount prerequisites={result.prerequisites} branch={result.branch} />
                </Space>
            )}
        </div>
    );
}

function alertType(result: VCSDirectPushCheckResult): 'success' | 'warning' | 'error' {
    if (result.status === 'blocked') {
        return 'error';
    }
    return result.status === 'warning' ? 'warning' : 'success';
}

function verdictMessage(operation: VCSDirectPushOperation, result: VCSDirectPushCheckResult): string {
    const isBranchCreation = operation === 'createBranch';
    // The branch name itself sits right below, in the count of prerequisites Runway checked
    const branch = isBranchCreation ? 'the branch Runway will create' : `your ${result.branchLabel}`;
    const policies = result.guidance.policiesLabel.toLowerCase();

    if (result.status === 'passed') {
        return `No blocking ${policies} found for ${branch}.`;
    }

    if (result.status === 'warning') {
        return `No definite blocker found for ${branch}, but some prerequisites need review.`;
    }

    const blocked = isBranchCreation ? `Runway can't create ${branch}.` : `Runway can't push directly to ${branch}.`;

    const blockingPolicies = new Set(
        result.prerequisites
            .filter((prerequisite) => prerequisite.status === 'blocked' && prerequisite.policy)
            .map((prerequisite) => prerequisite.policy?.label),
    );

    // Nothing to advise when the blocker isn't a policy, e.g. a missing repository permission
    if (blockingPolicies.size === 0) {
        return blocked;
    }

    return `${blocked} ${result.guidance.bypassAdvice}`;
}

interface PolicyGroup {
    policyLabel: string;
    policyUrl?: string;
    blocked: boolean;
    prerequisites: VCSDirectPushPrerequisite[];
}

function groupPrerequisitesByPolicy(prerequisites: VCSDirectPushPrerequisite[]): PolicyGroup[] {
    const groups: PolicyGroup[] = [];

    prerequisites
        .filter((prerequisite) => prerequisite.status !== 'passed')
        .forEach((prerequisite) => {
            const policyLabel = prerequisite.policy?.label ?? '';
            const existing = groups.find((group) => group.policyLabel === policyLabel);

            if (existing) {
                existing.prerequisites.push(prerequisite);
                existing.blocked = existing.blocked || prerequisite.status === 'blocked';
                existing.policyUrl = existing.policyUrl ?? prerequisite.policy?.url;
                return;
            }

            groups.push({
                policyLabel,
                policyUrl: prerequisite.policy?.url,
                blocked: prerequisite.status === 'blocked',
                prerequisites: [prerequisite],
            });
        });

    return groups;
}

function PolicyCard(props: { group: PolicyGroup; policyNoun: string }): JSX.Element {
    const { group, policyNoun } = props;

    return (
        <Space
            direction="vertical"
            size={4}
            style={{
                width: '100%',
                backgroundColor: runwayColors.white,
                border: `1px solid ${antGrey.grey4}`,
                borderRadius: 6,
                padding: 12,
            }}
        >
            {group.policyLabel && (
                <Text strong>
                    {group.blocked ? 'Blocked by' : 'Flagged by'} {group.policyLabel}
                </Text>
            )}

            <ul
                style={{
                    margin: 0,
                    paddingLeft: group.policyLabel ? 18 : 0,
                    listStyle: group.policyLabel ? undefined : 'none',
                }}
            >
                {group.prerequisites.map((prerequisite) => (
                    <li key={prerequisite.name}>
                        <Text>{prerequisite.name}</Text>
                        {prerequisite.explanation && (
                            <div>
                                <Text type="secondary">{prerequisite.explanation}</Text>
                            </div>
                        )}
                    </li>
                ))}
            </ul>

            {group.policyUrl && (
                <a href={group.policyUrl} target="_blank" rel="noreferrer">
                    View {policyNoun || 'policy'}
                </a>
            )}
        </Space>
    );
}

function CheckedCount({
    prerequisites,
    branch,
}: {
    prerequisites: VCSDirectPushPrerequisite[];
    branch: string;
}): JSX.Element | null {
    const uniqueRuleNames = [...new Set(prerequisites.map((prerequisite) => prerequisite.name))];

    if (uniqueRuleNames.length === 0) {
        return null;
    }

    return (
        <Text type="secondary">
            {uniqueRuleNames.length} prerequisites checked against <Text code>{branch}</Text>
            <Tooltip
                title={
                    <>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>Prerequisites checked</div>
                        <ul style={{ margin: 0, paddingLeft: 16 }}>
                            {uniqueRuleNames.map((name) => (
                                <li key={name}>{name}</li>
                            ))}
                        </ul>
                    </>
                }
            >
                <InfoCircleOutlined style={{ color: antGrey.grey7, marginLeft: 8 }} />
            </Tooltip>
        </Text>
    );
}

function CaveatSection(props: { caveat: VCSDirectPushCaveat }): JSX.Element {
    const { caveat } = props;

    return (
        <div>
            <SectionTitle title={caveat.title} />
            <Alert
                showIcon
                type="info"
                // Ensures we match the border from other banners since they'll appear together
                style={{ border: `1px solid ${runwayColors.primary}50` }}
                message={
                    <>
                        {caveat.message}
                        {caveat.docsUrl && (
                            <>
                                {' '}
                                <a href={caveat.docsUrl} target="_blank" rel="noreferrer">
                                    Learn more
                                </a>
                            </>
                        )}
                    </>
                }
            />
        </div>
    );
}

function SectionTitle(props: { title: string }): JSX.Element {
    return (
        <Text style={{ ...titleStyle, fontWeight: 400, fontSize: 14, marginBottom: 16, display: 'block' }}>
            {props.title}
        </Text>
    );
}
